import { config } from '@/lib/config'
import { useSessionStore } from '@/stores/sessionStore'
import { parseRetryAfter } from './retryAfter'
import { ApiError, type ApiErrorPayload, type RequestOptions } from './types'

/**
 * Minimal fetch wrapper.
 *
 * - Adds JSON headers by default.
 * - Uses `credentials: 'include'` so HttpOnly auth cookies round-trip.
 * - Serializes `options.params` into a querystring.
 * - Throws `ApiError` on non-2xx responses.
 * - On 401 (non-auth path): attempts POST /auth/refresh once, then retries.
 *   Concurrent 401s share the same refresh promise (no thundering herd).
 */

function appendParam(searchParams: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null) return
  if (Array.isArray(value)) {
    // FastAPI list[str] query: ?key=a&key=b 형태로 같은 key 반복.
    for (const v of value) {
      if (v !== undefined && v !== null) searchParams.append(key, String(v))
    }
    return
  }
  searchParams.append(key, String(value))
}

export function buildApiUrl(path: string, params?: RequestOptions['params']): string {
  const base = new URL(config.apiBaseUrl.replace(/\/+$/, ''), window.location.origin)
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const basePath = base.pathname.replace(/\/+$/, '')
  const pathAlreadyIncludesBase = cleanPath === basePath || cleanPath.startsWith(`${basePath}/`)
  const url = new URL(pathAlreadyIncludesBase ? cleanPath : `${basePath}${cleanPath}`, base.origin)

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      appendParam(url.searchParams, key, value)
    }
  }
  return url.toString()
}

async function parseError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload | undefined
  try {
    payload = (await response.json()) as ApiErrorPayload
  } catch {
    payload = undefined
  }
  const detail = payload?.detail
  const message =
    typeof detail === 'string'
      ? detail
      : detail && typeof detail === 'object' && 'message' in detail
        ? String((detail as { message?: unknown }).message ?? '')
        : detail && typeof detail === 'object'
          ? JSON.stringify(detail)
          : `HTTP ${response.status} ${response.statusText}`
  return new ApiError(
    response.status,
    message,
    payload,
    parseRetryAfter(response.headers.get('Retry-After')),
  )
}

// Singleton refresh promise: concurrent 401s share one refresh call.
let _refreshing: Promise<void> | null = null

export async function refreshSession(): Promise<void> {
  if (_refreshing) return _refreshing
  _refreshing = fetch(buildApiUrl('/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => {
      if (!r.ok) return Promise.reject(new ApiError(r.status, 'refresh_failed'))
    })
    .finally(() => {
      _refreshing = null
    })
  try {
    await _refreshing
  } catch (error) {
    useSessionStore.getState().setExpired(true)
    throw error
  }
}

/** Authenticated binary fetch with the same refresh/retry behavior as API calls. */
export async function fetchAuthenticatedBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  let response = await fetch(buildApiUrl(path), {
    credentials: 'include',
    signal,
  })
  if (response.status === 401) {
    await refreshSession()
    response = await fetch(buildApiUrl(path), {
      credentials: 'include',
      signal,
    })
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return response.blob()
}

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
  _retried = false,
): Promise<T> {
  const { body, params, headers, ...init } = options

  const finalHeaders = new Headers(headers)
  if (!finalHeaders.has('Accept')) {
    finalHeaders.set('Accept', 'application/json')
  }

  let finalBody: BodyInit | undefined
  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
      finalBody = body as BodyInit
    } else {
      finalBody = JSON.stringify(body)
      if (!finalHeaders.has('Content-Type')) {
        finalHeaders.set('Content-Type', 'application/json')
      }
    }
  }

  const response = await fetch(buildApiUrl(path, params), {
    ...init,
    headers: finalHeaders,
    body: finalBody,
    credentials: init.credentials ?? 'include',
  })

  if (!response.ok) {
    // 401 on a protected endpoint → try refresh once, then retry the original request.
    if (response.status === 401 && !path.startsWith('/auth/') && !_retried) {
      try {
        await refreshSession()
        return request<T>(path, options, true)
      } catch {
        useSessionStore.getState().setExpired(true)
        throw await parseError(response)
      }
    }
    if (response.status === 401 && !path.startsWith('/auth/')) {
      useSessionStore.getState().setExpired(true)
    }
    throw await parseError(response)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return (await response.json()) as T
  }
  return (await response.text()) as unknown as T
}

/** Convenience shorthands. */
export const apiClient = {
  get: <T = unknown>(path: string, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T = unknown>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'body' | 'method'>,
  ) => request<T>(path, { ...options, method: 'POST', body }),
  put: <T = unknown>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'body' | 'method'>,
  ) => request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T = unknown>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'body' | 'method'>,
  ) => request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T = unknown>(path: string, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
